import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, DatePicker, InputNumber, Select, Row, Col, Switch } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as alertConditionApi from '../../../services/alertConditionApi';

const { Option } = Select;

const prefectures = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];

const NotificationPropertyForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const isEditMode = id !== 'new' && id !== undefined;

  // 坪単価から平米単価を自動計算する関数
  const calculatePricePerM2 = () => {
    const pricePerTsuboMin = form.getFieldValue('price_per_tsubo_min');
    const pricePerTsuboMax = form.getFieldValue('price_per_tsubo_max');
    
    // 1坪 = 3.30579㎡
    const TSUBO_TO_M2 = 3.30579;
    
    let calculatedPrice = null;
    
    if (pricePerTsuboMin && pricePerTsuboMax) {
      // 両方の値がある場合は平均値を計算
      const avgTsuboPrice = (pricePerTsuboMin + pricePerTsuboMax) / 2;
      calculatedPrice = Math.round(avgTsuboPrice / TSUBO_TO_M2);
    } else if (pricePerTsuboMin) {
      // 最小値のみがある場合
      calculatedPrice = Math.round(pricePerTsuboMin / TSUBO_TO_M2);
    } else if (pricePerTsuboMax) {
      // 最大値のみがある場合
      calculatedPrice = Math.round(pricePerTsuboMax / TSUBO_TO_M2);
    }
    
    if (calculatedPrice) {
      form.setFieldValue('price_per_m2', calculatedPrice);
    }
  };

  useEffect(() => {
    if (isEditMode && id) {
      loadPropertyData();
    } else {
      // 新規作成時の初期値設定
      form.setFieldsValue({
        registered_at: dayjs(), // 現在日時を設定
        is_active: true, // デフォルトでアクティブ
      });
    }
  }, [id, isEditMode, form]);

  const loadPropertyData = async () => {
    try {
      setLoading(true);
      const data = await alertConditionApi.getById(Number(id));

      // フォームにデータをセット
      form.setFieldsValue({
        ...data,
        registered_at: data.registered_at ? dayjs(data.registered_at) : null,
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoading(true);

      // DatePickerの値を文字列に変換
      const formattedValues = {
        ...values,
        registered_at: values.registered_at ? values.registered_at.format('YYYY-MM-DD') : null,
      };

      const result = isEditMode && id
        ? await alertConditionApi.update(Number(id), formattedValues)
        : await alertConditionApi.create(formattedValues);

      if (result.validationErrors) {
        const fieldErrors = Object.entries(result.validationErrors).map(
          ([name, messages]) => ({
            name,
            errors: messages,
          })
        );
        form.setFields(fieldErrors);
        message.error('入力内容にエラーがあります。各項目を確認してください。');
        return;
      }

      message.success(isEditMode ? '更新しました' : '登録しました');
      navigate('/notification-properties');
    } catch (error) {
      console.error('Failed to submit:', error);
      message.error(isEditMode ? '更新に失敗しました' : '登録に失敗しました');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="dark-theme-container">
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/notification-properties')}
          style={{ marginBottom: 16 }}
        >
          一覧に戻る
        </Button>
        <h1 className="dark-title">{isEditMode ? '通知物件編集' : '通知物件新規登録'}</h1>
      </div>

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="登録タイトル"
                name="title"
                rules={[{ required: true, message: '登録タイトルを入力してください' }]}
              >
                <Input placeholder="例：渋谷区の3LDK通知条件" size="large" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="登録日"
                name="registered_at"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY年MM月DD日"
                  placeholder="登録日を選択"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="都道府県"
                name="prefecture"
                rules={[{ required: true, message: '都道府県を選択してください' }]}
              >
                <Select placeholder="都道府県を選択" size="large" showSearch>
                  {prefectures.map(pref => (
                    <Option key={pref} value={pref}>{pref}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="区（市区名）"
                name="district"
                rules={[{ required: true, message: '区を入力してください' }]}
              >
                <Input placeholder="例：渋谷区" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="平米数（㎡）" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={11}>
                <Form.Item
                  name="area_m2_min"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最小"
                    min={0}
                    step={0.01}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={2} style={{ textAlign: 'center', lineHeight: '40px' }}>
                <span style={{ fontSize: '16px' }}>～</span>
              </Col>
              <Col span={11}>
                <Form.Item
                  name="area_m2_max"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最大"
                    min={0}
                    step={0.01}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item label="駅徒歩（分）" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={11}>
                <Form.Item
                  name="walk_minutes_min"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最小"
                    min={0}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={2} style={{ textAlign: 'center', lineHeight: '40px' }}>
                <span style={{ fontSize: '16px' }}>～</span>
              </Col>
              <Col span={11}>
                <Form.Item
                  name="walk_minutes_max"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最大"
                    min={0}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item label="坪単価（万円）" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={11}>
                <Form.Item
                  name="price_per_tsubo_min"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最小"
                    min={0}
                    size="large"
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value!.replace(/\,/g, '') as any}
                    onChange={calculatePricePerM2}
                  />
                </Form.Item>
              </Col>
              <Col span={2} style={{ textAlign: 'center', lineHeight: '40px' }}>
                <span style={{ fontSize: '16px' }}>～</span>
              </Col>
              <Col span={11}>
                <Form.Item
                  name="price_per_tsubo_max"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最大"
                    min={0}
                    size="large"
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value!.replace(/\,/g, '') as any}
                    onChange={calculatePricePerM2}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Form.Item
            label="平米単価（万円/㎡）"
            name="price_per_m2"
            tooltip="坪単価から自動計算されます。手動で変更も可能です。"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="平米単価を入力（自動計算）"
              min={0}
              size="large"
              formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
              parser={value => value!.replace(/\,/g, '') as any}
            />
          </Form.Item>

          <Form.Item label="築年数（年）" style={{ marginBottom: 0 }}>
            <Row gutter={8}>
              <Col span={11}>
                <Form.Item
                  name="built_year_min"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最小"
                    min={0}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={2} style={{ textAlign: 'center', lineHeight: '40px' }}>
                <span style={{ fontSize: '16px' }}>～</span>
              </Col>
              <Col span={11}>
                <Form.Item
                  name="built_year_max"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="最大"
                    min={0}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="ステータス"
                name="is_active"
                valuePropName="checked"
                tooltip="無効にすると通知が停止されます"
              >
                <Switch checkedChildren="アクティブ" unCheckedChildren="無効" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <Button
                size="large"
                onClick={() => navigate('/notification-properties')}
              >
                キャンセル
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitLoading}
                icon={<SaveOutlined />}
                size="large"
              >
                {isEditMode ? '更新' : '登録'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default NotificationPropertyForm;
